import React, { useState, useEffect } from "react";

import {
  Alert,
  Button,
  ButtonGroup,
  Chip,
  Divider,
  Grid,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
  FormLabel,
} from "@mui/material";

import {
  updateStartDate,
  updateEndDate,
  setShowFilters,
} from "../redux/dateRangeSlice";
import { updateFeedTopics } from "../redux/feedSlice";
import { FEED_CHUNKS, WHOLE_DATASET_FEED_CHUNK } from "../constants/FeedConsts";
import { useDispatch, useSelector } from "react-redux";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

export default function DateFilters() {
  const mode = sessionStorage.getItem("mode");
  const getFeedChunks = () => {
    return [WHOLE_DATASET_FEED_CHUNK, ...FEED_CHUNKS];
  };

  const startTimeRedux = useSelector((state) => state.dateRange.startDate);
  const endTimeRedux = useSelector((state) => state.dateRange.endDate);

  const [startDate, setStartDate] = useState(startTimeRedux);
  const [endDate, setEndDate] = useState(endTimeRedux);

  const getCurrentChunk = () => {
    const currChunk = getFeedChunks().find(
      (chunk) => chunk.start === startDate && chunk.end === endDate
    );
    return currChunk ? currChunk.dateString : null;
  };

  const [selectedChunk, setSelectedChunk] = useState(getCurrentChunk());
  const [disableFilters, setDisableFilters] = useState(false);

  const dispatch = useDispatch();

  const handleStartDateSelect = (event) => {
    if (event && event.$d !== null) {
      setStartDate(event.$d.getTime());
    }
  };

  const handleEndDateSelect = (event) => {
    if (event && event.$d !== null) {
      setEndDate(event.$d.getTime());
    }
  };

  const handleRadioChange = (event) => {
    const selectedChunk = event.target.value;
    const chunk = getFeedChunks().find(
      (chunk) => chunk.dateString === selectedChunk
    );
    if (chunk) {
      setStartDate(chunk.start);
      setEndDate(chunk.end);
      setSelectedChunk(selectedChunk);
    } else {
      console.error("No feed chunk found for date string: ", selectedChunk);
    }
  };

  const handleRestoreDefault = () => {
    setSelectedChunk(WHOLE_DATASET_FEED_CHUNK.dateString);
    setEndDate(WHOLE_DATASET_FEED_CHUNK.end);
    setStartDate(WHOLE_DATASET_FEED_CHUNK.start);
  };

  const handleSaveFilters = () => {
    dispatch(updateStartDate(startDate));
    dispatch(updateEndDate(endDate));
    dispatch(setShowFilters(false));

    // Change feed items list based on selected date range
    let feedTopics = [];
    if (
      startDate === WHOLE_DATASET_FEED_CHUNK.start &&
      endDate === WHOLE_DATASET_FEED_CHUNK.end
    ) {
      feedTopics = JSON.parse(sessionStorage.getItem("bakedFeedTopics"));
    } else {
      const automatedFeedTopics = JSON.parse(
        sessionStorage.getItem("automatedFeedTopics")
      );
      feedTopics = automatedFeedTopics.filter((topic) => {
        const topicStartTime = topic["queryDateRange"]["startTimeEpochMillis"];
        const topicEndTime = topic["queryDateRange"]["endTimeEpochMillis"];
        return topicEndTime >= startDate && topicStartTime <= endDate;
      });
    }
    dispatch(updateFeedTopics(feedTopics));
  };

  useEffect(() => {
    console.log("startDate in redux: ", new Date(startTimeRedux).toISOString());
    console.log("endDate in redux: ", new Date(endTimeRedux).toISOString());
  }, []);

  return (
    <Grid container spacing={2} sx={{ p: 2 }}>
      <Grid item xs={12}>
        {disableFilters && (
          <Alert severity="error">
            Filters can not be changed in this view. Please start a new query to
            set new date filters.
          </Alert>
        )}
      </Grid>
      <Grid item xs={12}>
        <FormControl>
          <FormLabel
            id="select-period"
            sx={{
              fontFamily: "Roboto Condensed",
              fontWeight: 600,
              color: mode === "light" ? "#000000" : "#FFFFFF",
            }}
          >
            Select Feed
          </FormLabel>
          <RadioGroup
            defaultValue={selectedChunk}
            value={selectedChunk}
            aria-labelledby="select-period"
            name="radio-buttons-group"
            onChange={handleRadioChange}
          >
            {getFeedChunks().map((chunk) => (
              <FormControlLabel
                key={chunk.dateString}
                value={chunk.dateString}
                control={<Radio size="small" />}
                label={chunk.dateString}
                sx={{ "& .MuiFormControlLabel-label": { fontSize: "14px" } }}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <Divider>
          <Chip label="OR" size="small" />
        </Divider>
      </Grid>

      <Grid item xs={12}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <FormControl>
            <FormLabel
              id="select-range"
              sx={{
                fontFamily: "Roboto Condensed",
                fontWeight: 600,
                color: mode === "light" ? "#000000" : "#FFFFFF",
                mb: 2,
              }}
            >
              Custom Date Range:
            </FormLabel>
            <Grid container spacing={2}>
              <Grid item xs={12} md={12}>
                <DatePicker
                  disabled={disableFilters}
                  value={
                    startDate > 0
                      ? dayjs(new Date(startDate).toISOString())
                      : null
                  }
                  slotProps={{
                    actionBar: {
                      actions: ["clear"],
                    },
                    textField: { fullWidth: true, size: "small" },
                    field: { clearable: true },
                  }}
                  label="Start Date"
                  onChange={handleStartDateSelect}
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <DatePicker
                  disabled={disableFilters}
                  value={
                    endDate > 0 ? dayjs(new Date(endDate).toISOString()) : null
                  }
                  slotProps={{
                    actionBar: {
                      actions: ["clear"],
                    },
                    textField: { fullWidth: true, size: "small" },
                    field: { clearable: true },
                  }}
                  label="End Date"
                  onChange={handleEndDateSelect}
                />
              </Grid>
            </Grid>
          </FormControl>
        </LocalizationProvider>
      </Grid>

      <Grid
        container
        spacing={2}
        sx={{
          mt: 1,
          paddingLeft: 2,
        }}
      >
        <Grid item xs={12}>
          <ButtonGroup fullWidth sx={{ borderRadius: 24 }}>
            <Button
              disabled={disableFilters}
              fullWidth
              variant="outlined"
              onClick={handleRestoreDefault}
              sx={{
                "&:hover": {
                  background: "#efefef",
                  color: "#AC1FFF",
                  borderColor: "#aaaaaa",
                },
                display: "flex",
                borderRadius: 12,
                fontSize: 14,
                fontWeight: 700,
                letterSpacing: "-0.5px",
                boxShadow: 0,
                marginTop: 2,
                marginBottom: 1,
                background: "#ffffff",
                color: "#666666",
                borderColor: "#aaaaaa",
                textTransform: "none",
              }}
            >
              Restore Default
            </Button>
            <Button
              disabled={disableFilters}
              fullWidth
              onClick={handleSaveFilters}
              variant="contained"
              sx={{
                "&:hover": {
                  background: "#AC1FFF",
                  color: "#FFFFFF",
                },
                display: "flex",
                borderRadius: 12,
                fontSize: 14,
                fontWeight: 700,
                letterSpacing: "-0.5px",
                boxShadow: 0,
                marginTop: 2,
                marginBottom: 1,
                background: "#AC1FFF",
                color: "#FFFFFF",
                textTransform: "none",
              }}
            >
              Save & Apply
            </Button>
          </ButtonGroup>
        </Grid>
      </Grid>
    </Grid>
  );
}
